import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Calculator as CalcIcon } from "lucide-react";
import { CALC_SIZES, CALC_TASKS, PLANS, planLabel } from "../../content/site";
import { Reveal } from "./motion";
import { scrollToId } from "./useLenis";

const fmt = (h) => h.toLocaleString("de-DE", { maximumFractionDigits: 2 });

const Chip = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-2xl border px-5 py-4 text-left transition-[background-color,border-color,color] duration-300 ${
      active ? "border-[#1E5631] bg-[#1E5631] text-[#FBF9F4]" : "border-[#E2DDD0] bg-[#FBF9F4] text-[#1B281E] hover:border-[#1E5631]"
    }`}
  >
    {children}
  </button>
);

const Result = ({ hours }) => {
  if (hours === 0) {
    return <p data-testid="calc-result-empty" className="text-[#FBF9F4]/85">Wählen Sie mindestens eine Arbeit aus, dann zeigen wir Ihnen ein passendes Abo.</p>;
  }
  const plan = PLANS.find((p) => p.hours >= hours);
  const send = (detail) => {
    window.dispatchEvent(new CustomEvent("gw:service", { detail }));
    scrollToId("kontakt");
  };
  return (
    <motion.div key={plan?.key ?? "custom"} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <p className="text-sm text-[#FBF9F4]/85">Geschätzter Aufwand: <span data-testid="calc-hours" className="font-semibold text-[#FBF9F4]">ca. {fmt(hours)} Std. im Monat</span></p>
      {plan ? (
        <>
          <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#DCEFBE]">Unsere Empfehlung</p>
          <p data-testid="calc-recommendation" className="mt-1 font-display text-4xl text-[#FBF9F4] sm:text-5xl">{plan.hours} Stunden · {plan.price} €</p>
          <p className="mt-1 text-[#FBF9F4]/85">monatlich</p>
          <button data-testid="calc-cta" onClick={() => send(planLabel(plan))} className="btn-light group mt-6">
            Dieses Abo anfragen
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#1E5631] text-[#FBF9F4] transition-transform duration-300 group-hover:rotate-[-45deg]"><ArrowRight className="h-4 w-4" /></span>
          </button>
        </>
      ) : (
        <>
          <p data-testid="calc-recommendation" className="mt-4 font-display text-3xl text-[#FBF9F4] sm:text-4xl">Individuelles Angebot</p>
          <p className="mt-2 max-w-sm text-[#FBF9F4]/85">Ihr Garten braucht voraussichtlich mehr als 6 Stunden im Monat. Wir stimmen gern ein passendes Angebot mit Ihnen ab.</p>
          <button data-testid="calc-cta" onClick={() => send("Sonstiges")} className="btn-light group mt-6">
            Angebot anfragen
            <span className="grid h-8 w-8 place-items-center rounded-full bg-[#1E5631] text-[#FBF9F4]"><ArrowRight className="h-4 w-4" /></span>
          </button>
        </>
      )}
    </motion.div>
  );
};

export const Calculator = () => {
  const [size, setSize] = useState("mittel");
  const [tasks, setTasks] = useState(["rasen", "hecke"]);
  const toggle = (k) => setTasks((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));
  const hours = CALC_TASKS.filter((t) => tasks.includes(t.key)).reduce((s, t) => s + t.hours[size], 0);

  return (
    <Reveal className="mt-10">
      <div data-testid="hours-calculator" className="grid overflow-hidden rounded-[28px] border border-[#E2DDD0] bg-white lg:grid-cols-12">
        <div className="p-7 md:p-10 lg:col-span-7">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#C86D51]"><CalcIcon className="h-4 w-4" /> Stunden-Rechner</p>
          <h4 className="mt-3 font-display text-2xl text-[#1B281E] sm:text-3xl">Welches Abo passt zu Ihrem Garten?</h4>
          <p className="mt-8 text-sm font-medium text-[#1B281E]">1. Wie groß ist Ihr Garten ungefähr?</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {CALC_SIZES.map((s) => (
              <Chip key={s.key} testId={`calc-size-${s.key}`} active={size === s.key} onClick={() => setSize(s.key)}>
                <span className="block font-semibold">{s.label}</span>
                <span className={`block text-sm ${size === s.key ? "text-[#FBF9F4]/85" : "text-[#4B5B4E]"}`}>{s.hint}</span>
              </Chip>
            ))}
          </div>
          <p className="mt-8 text-sm font-medium text-[#1B281E]">2. Was soll regelmäßig gemacht werden?</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {CALC_TASKS.map((t) => (
              <Chip key={t.key} testId={`calc-task-${t.key}`} active={tasks.includes(t.key)} onClick={() => toggle(t.key)}>
                <span className="flex items-center gap-3">
                  <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border ${tasks.includes(t.key) ? "border-[#DCEFBE] bg-[#DCEFBE] text-[#1E5631]" : "border-[#C5BC9F]"}`}>
                    {tasks.includes(t.key) && <Check className="h-4 w-4" />}
                  </span>
                  {t.label}
                </span>
              </Chip>
            ))}
          </div>
        </div>
        <div className="relative flex flex-col justify-between gap-8 bg-[#0D2F18] p-7 md:p-10 lg:col-span-5">
          <div className="grain absolute inset-0" />
          <div className="relative"><Result hours={hours} /></div>
          <p data-testid="calc-disclaimer" className="relative text-xs leading-relaxed text-[#FBF9F4]/75">
            Grobe Orientierung, kein verbindliches Angebot. Den tatsächlichen Aufwand stimmen wir nach einer Besichtigung mit Ihnen ab.
          </p>
        </div>
      </div>
    </Reveal>
  );
};
